import { Modal, ModalInner, Text, Button } from 'components';
import { BadgeCheckIcon, XCircleOutlineIcon } from 'assets/svgs';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  onDelete?: () => void;
  onEdit?: () => void;
  onToggleStatus?: () => void;
  onUnlock?: () => void;
}

interface ListElementProps {
  title: string;
  value: string | number;
}

const UserDetailModalListElement = ({ title, value }: ListElementProps) => {
  return (
    <li className="border-primary rounded-lg border px-3 py-2">
      <Text weight="regular" size="body2" color="tertiary">
        {title}
      </Text>
      <Text weight="regular" size="body2" color="primary">
        {value}
      </Text>
    </li>
  );
};

const UserDetailModal = ({ isOpen, onClose, user, onDelete, onEdit, onToggleStatus, onUnlock }: Props) => {
  if (!user) return null;

  const userDetailList = [
    { title: 'Name', key: 'name' },
    { title: 'Email', key: 'email' },
    { title: 'Company', key: 'company' },
    { title: 'Country', key: 'country' },
    { title: 'Purpose', key: 'purpose' },
    { title: 'Login', key: 'loginMethod' },
    { title: 'Status', key: 'status' },
    { title: 'Role', key: 'role' },
    { title: 'Joined Date', key: 'joinedDate' },
    { title: 'Last Login', key: 'lastLogin' },
    { title: 'Verified', key: 'verified' },
    { title: 'Account Unlock Date', key: 'lockReleaseDate' },
    { title: 'Privacy Agreement', key: 'privacyAgree' },
    { title: 'Marketing Agreement', key: 'marketingAgree' },
    { title: 'Toss Agreement', key: 'tossAgree' },
  ];

  const getValue = (key: string) => {
    const value = user[key];
    if (key === 'role') {
      return Array.isArray(value) ? value.join(', ') : value;
    }
    if (key === 'marketingAgree' || key === 'privacyAgree' || key === 'tossAgree') {
      return value ? 'Agree' : 'Disagree';
    }
    return value !== undefined && value !== null ? value : '-';
  };

  const getStatusElement = (status: string) => {
    const userStatusStyles = {
      Active: { title: 'Active', className: 'bg-[#E6FEED] border-[#27B72D] text-[#27B72D]' },
      Inactive: { title: 'Inactive', className: 'bg-white border-[#8F8F8F] text-[#8F8F8F]' },
      Locked: { title: 'Locked', className: 'bg-[#FEE6E6] border-[#F30808] text-[#F30808]' },
    };
    
    const style = userStatusStyles[status as keyof typeof userStatusStyles] || userStatusStyles.Inactive;
    
    return (
      <span className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium ${style.className}`}>
        {style.title}
      </span>
    );
  };

  const getRoleElement = (role: string) => {
    const userRoleStyles = {
      ROLE_USER: { title: 'USER', className: 'bg-[#E6F2FE] border-[#0890F3] text-[#0890F3]' },
      ROLE_ADMIN: { title: 'ADMIN', className: 'bg-[#FFF3E0] border-[#FF9800] text-[#FF9800]' },
    };
    
    const style = userRoleStyles[role as keyof typeof userRoleStyles] || userRoleStyles.ROLE_USER;
    
    return (
      <span className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium ${style.className}`}>
        {style.title}
      </span>
    );
  };

  const getUnlockDateElement = () => {
    const unlockDate = user.lockReleaseDate;
    const isLocked = user.status === 'Locked';
    
    return (
      <div className="flex items-center justify-between">
        <Text weight="regular" size="body2" color="primary">
          {unlockDate || '-'}
        </Text>
        {isLocked && (
          <Button
            text="Now Unlock"
            onClick={() => onUnlock?.()}
            type="blue"
            size="small"
            className="ml-2"
          />
        )}
      </div>
    );
  };

  const getVerifiedElement = () => {
    return user.verified ? (
      <BadgeCheckIcon className="w-5 h-5 [&_path]:fill-green-600" />
    ) : (
      <XCircleOutlineIcon className="w-5 h-5 [&_path]:stroke-red-500" />
    );
  };

  return (
    <Modal isOpened={isOpen}>
      <ModalInner
        title={user.email}
        onClickClose={onClose}
      >
        <>
          <ul className="flex max-h-[60vh] flex-col gap-4 overflow-auto px-4 pb-4">
            {userDetailList.map(({ title, key }) => (
              <li key={key} className="border-primary rounded-lg border px-3 py-2">
                <Text weight="regular" size="body2" color="tertiary">
                  {title}
                </Text>
                {key === 'verified' ? (
                  getVerifiedElement()
                ) : key === 'status' ? (
                  getStatusElement(user.status)
                ) : key === 'role' ? (
                  getRoleElement(user.role)
                ) : key === 'lockReleaseDate' ? (
                  getUnlockDateElement()
                ) : (
                  <Text weight="regular" size="body2" color="primary">
                    {getValue(key)}
                  </Text>
                )}
              </li>
            ))}
          </ul>
          <div className="flex w-full flex-row items-center justify-end bg-bg-transparent_gray_light p-4">
            {user.status === 'Inactive' ? (
              <Button 
                text="Activate" 
                onClick={onToggleStatus} 
                type="blue" 
                size="large" 
              />
            ) : (
              <Button 
                text="Deactivate" 
                onClick={onToggleStatus} 
                type="secondary" 
                size="large" 
                className="bg-red-600 border-red-600 hover:bg-red-700 text-white"
              />
            )}
          </div>
        </>
      </ModalInner>
    </Modal>
  );
};

export default UserDetailModal; 